import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Input,
  SingleSelect,
  TrashAlt,
  Toastify,
  GripVertical,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DatePickerDateRange,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Warning,
  InfoCircle,
} from 'lift-ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Interface for Pacing Date Range API response
interface PacingDateRange {
  brand_id: number;
  min_date: string | null;
  max_date: string | null;
  has_data: boolean;
  adjusted_start_date: string | null;
  adjusted_end_date: string | null;
  note: string | null;
}

import { apiClient } from '@lift-admin/utils';
import { formatDateToMMDDYYYY } from '../utils/date';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { BrandConfig, BrandThreshold } from '../types';
import { dmgAlertApiService } from '../utils/dmgAlertApiService';
import {
  getBorderClasses,
  hasConnectionToNext as checkConnectionToNext,
  hasConnectionFromPrev as checkConnectionFromPrev,
} from '../utils/thresholdFilters';

import { validateThreshold } from './BrandThresholdTable';

const DMG_ALERT_API_BASE_URL = process.env.DMG_ALERT_API_URL;
// Helper function to get error key for a threshold
const getThresholdErrorKey = (brandId: number, thresholdId: string) => `${brandId}-${thresholdId}`;

// Helper function to convert label to snake_case apiValue
const labelToApiValue = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .trim();
};

// Helper function to get apiValue from a preset, falling back to converted label
const getPresetApiValue = (preset: any): string => {
  if (preset.apiValue) return preset.apiValue;
  if (preset.label) return labelToApiValue(preset.label);
  return '';
};

// Helper function to extract dates from threshold.endDate (format: "startISO|endISO")
const extractDatesFromThreshold = (
  endDate: string | undefined
): { startDate: Date; endDate: Date } | null => {
  if (!endDate || !endDate.includes('|')) return null;
  try {
    const [startISO, endISO] = endDate.split('|');
    const start = new Date(startISO);
    const end = new Date(endISO);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return { startDate: start, endDate: end };
  } catch {
    return null;
  }
};

// Helper function to normalize a value for comparison (lowercase, trim)
const normalizeValue = (value: string): string => {
  return value.toLowerCase().trim();
};

// Helper function to find preset by stored value
const findPresetByValue = (presets: any[], storedValue: string | undefined): any | undefined => {
  if (!storedValue || !presets.length) return undefined;

  // First try exact match (like v1 - this is what was working)
  let preset = presets.find((p: any) => getPresetApiValue(p) === storedValue);

  // If exact match fails, try normalized match (for case/whitespace differences)
  if (!preset) {
    const normalizedStored = normalizeValue(storedValue);
    preset = presets.find((p: any) => {
      const apiValue = getPresetApiValue(p);
      return normalizeValue(apiValue) === normalizedStored;
    });
  }

  return preset;
};

// Transform sheet-config dimensions to payload format for API
const convertDimensionsToPayloadFormat = (
  dimensions: Record<string, string[]>
): Array<{
  name: string;
  type: string;
  value: string[];
  category: string;
  dimension_id: null;
}> => {
  const result: Array<{
    name: string;
    type: string;
    value: string[];
    category: string;
    dimension_id: null;
  }> = [];

  Object.entries(dimensions).forEach(([dimType, values]) => {
    if (dimType === 'All') return;
    if (Array.isArray(values)) {
      values.forEach(value => {
        result.push({
          name: value,
          type: dimType,
          value: [],
          category: 'standard',
          dimension_id: null,
        });
      });
    }
  });

  return result;
};

type SheetConfig = {
  id: number;
  type: string;
  metrics: string[];
  dimensions: Record<string, string[]>;
};

const getDimensionCombinationLabel = (dimensions: Record<string, string[]>): string => {
  const parts: string[] = [];
  Object.entries(dimensions).forEach(([dimType, values]) => {
    if (dimType !== 'All' && Array.isArray(values) && values.length > 0) {
      parts.push(`${dimType}: ${values.join(', ')}`);
    }
  });
  return parts.length > 0 ? parts.join(' | ') : 'All Dimensions';
};

// Helper to check if dimensions represent "All Dimension"
const isAllDimension = (
  dimensions: Record<string, string[]> | Array<{ name: string; type: string }>
): boolean => {
  if (Array.isArray(dimensions)) {
    // API format: [{name: "All", type: "All"}, ...]
    return dimensions.length === 1 && dimensions[0].name === 'All' && dimensions[0].type === 'All';
  } else {
    // Sheet config format: {"All": ["All Dimension"]} or only has "All" key
    const keys = Object.keys(dimensions);

    // Empty object is NOT "All Dimension"
    if (keys.length === 0) return false;

    // Only "All" key with values = "All Dimension"
    if (keys.length === 1 && keys[0] === 'All') {
      const allValues = dimensions['All'];
      return Array.isArray(allValues) && allValues.length > 0;
    }

    // Check if only "All" key has values and others are empty
    const hasAllWithValues =
      dimensions['All'] && Array.isArray(dimensions['All']) && dimensions['All'].length > 0;
    const nonAllEntries = Object.entries(dimensions).filter(
      ([dimType, values]) => dimType !== 'All' && Array.isArray(values) && values.length > 0
    );

    return hasAllWithValues && nonAllEntries.length === 0;
  }
};

// Helper to create a normalized dimension key from dimension data (API format or sheet config format)
const createDimensionKey = (
  dimensions: Record<string, string[]> | Array<{ name: string; type: string }>
): string => {
  // Special case: "All Dimension"
  if (isAllDimension(dimensions)) {
    return 'ALL_DIMENSION';
  }

  const dimMap: Record<string, string[]> = {};

  if (Array.isArray(dimensions)) {
    // API format: [{name: "SEM", type: "Type"}, ...]
    dimensions.forEach((dim: { name: string; type: string }) => {
      if (dim.name && dim.type && dim.type !== 'All' && dim.name !== 'All') {
        const parent = dim.type.trim();
        const child = dim.name.trim();
        if (!dimMap[parent]) dimMap[parent] = [];
        dimMap[parent].push(child);
      }
    });
  } else {
    // Sheet config format: {Platform: ["Google"], Tactic: ["Brand"], ...}
    Object.entries(dimensions).forEach(([dimType, values]) => {
      if (dimType !== 'All' && Array.isArray(values) && values.length > 0) {
        dimMap[dimType] = values.map(v => v.trim());
      }
    });
  }

  // Create sorted key for consistent matching
  return Object.entries(dimMap)
    .filter(([, values]) => values.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dimType, values]) => `${dimType}:${values.sort().join(',')}`)
    .join('|');
};

// Helper to find matching sheet config ID for loaded dimension data
const findMatchingSheetConfigId = (
  thresholdDimension: string | undefined,
  thresholdDimensionValue: string | undefined,
  sheetConfigs: SheetConfig[]
): string => {
  if (!sheetConfigs.length) return thresholdDimension || '';

  // First check if thresholdDimension is already a valid sheet config ID
  const directMatch = sheetConfigs.find(cfg => String(cfg.id) === thresholdDimension);
  if (directMatch) return thresholdDimension!;

  // Try to parse dimensionValue (JSON array of dimension objects from API)
  let apiDimensions: Array<{ name: string; type: string }> | null = null;
  if (thresholdDimensionValue) {
    try {
      const parsed = JSON.parse(thresholdDimensionValue);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name && parsed[0].type) {
        apiDimensions = parsed;
      }
    } catch {
      // Not valid JSON
    }
  }

  // If we have API dimensions, create a key and match against sheet configs
  if (apiDimensions) {
    const apiKey = createDimensionKey(apiDimensions);
    for (const cfg of sheetConfigs) {
      const cfgKey = createDimensionKey(cfg.dimensions);
      if (apiKey === cfgKey) {
        return String(cfg.id);
      }
    }
  }

  // Check if thresholdDimension represents "All Dimension" (stored as "All:All Dimension" by mapper or "ALL_DIMENSION")
  const isAllDimensionKey =
    thresholdDimension === 'All:All Dimension' ||
    thresholdDimension === 'All Dimension' ||
    thresholdDimension === 'All' ||
    thresholdDimension === 'ALL_DIMENSION';
  if (isAllDimensionKey) {
    // Find sheet config that represents "All Dimension"
    for (const cfg of sheetConfigs) {
      if (isAllDimension(cfg.dimensions)) {
        return String(cfg.id);
      }
    }
    // If no sheet config exists, return synthetic value
    return 'ALL_DIMENSION';
  }

  // Also check if apiDimensions represent "All Dimension"
  if (apiDimensions && isAllDimension(apiDimensions)) {
    // Find sheet config that represents "All Dimension"
    for (const cfg of sheetConfigs) {
      if (isAllDimension(cfg.dimensions)) {
        return String(cfg.id);
      }
    }
    // If no sheet config exists, return synthetic value
    return 'ALL_DIMENSION';
  }

  // Fallback: try to match the stored dimension key against sheet config keys
  if (thresholdDimension && thresholdDimension.includes(':')) {
    // thresholdDimension might already be a dimension key like "Platform:Google|Tactic:Brand"
    for (const cfg of sheetConfigs) {
      const cfgKey = createDimensionKey(cfg.dimensions);
      if (thresholdDimension === cfgKey) {
        return String(cfg.id);
      }
    }
  }

  // No match found, return original value
  return thresholdDimension || '';
};

// Types for drag-to-fill feature
type FillableColumn =
  | 'dimension'
  | 'metric'
  | 'operator'
  | 'threshold'
  | 'startDate'
  | 'endDate'
  | 'pacingValueType';

interface DragFillState {
  isActive: boolean;
  sourceRowIndex: number;
  sourceColumn: FillableColumn;
  targetRowIndices: number[];
  mouseY?: number;
}

interface CellSelectionState {
  rowIndex: number;
  column: FillableColumn;
}

// Sortable Threshold Row Component
interface SortableThresholdRowProps {
  threshold: BrandThreshold;
  index: number;
  originalIndex: number;
  isLastRow: boolean;
  hasConnectionToNext: boolean;
  hasConnectionFromPrev: boolean;
  children: React.ReactNode;
  logicalOperatorRow?: React.ReactNode;
}

const SortableThresholdRow: React.FC<SortableThresholdRowProps> = ({
  threshold,
  hasConnectionToNext,
  hasConnectionFromPrev,
  children,
  logicalOperatorRow,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: threshold.id,
  });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const transformedLogicalOperatorRow = logicalOperatorRow
    ? React.cloneElement(
        logicalOperatorRow as React.ReactElement<{ style?: React.CSSProperties }>,
        {
          style: {
            ...((logicalOperatorRow as React.ReactElement<{ style?: React.CSSProperties }>).props
              ?.style || {}),
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
          },
        }
      )
    : null;

  return (
    <React.Fragment>
      <tr ref={setNodeRef} style={rowStyle}>
        {/* Drag Handle Column */}
        <td
          className={`border-l border-r border-gray-300 ${hasConnectionFromPrev ? '' : 'border-t'} ${hasConnectionToNext ? '' : 'border-b'} px-1 py-2 bg-gray-50/50`}
        >
          <div className="flex items-center justify-center">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors duration-150 p-1 rounded hover:bg-gray-100"
              title="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          </div>
        </td>
        {children}
      </tr>
      {transformedLogicalOperatorRow}
    </React.Fragment>
  );
};

interface PacingThresholdTableProps {
  brandConfig: BrandConfig;
  onUpdateThreshold: (
    brandId: number,
    thresholdIndex: number,
    field: keyof BrandThreshold,
    value: string | number
  ) => void;
  onAddThresholdRow: (brandId: number) => void;
  onRemoveThresholdRow: (brandId: number, thresholdIndex: number) => void;
  onReorderThresholds: (brandId: number, fromIndex: number, toIndex: number) => void;
  onClearAllThresholds: (brandId: number) => void;
  isLoadingInitialData?: boolean;
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
    default_threshold_config?: Array<{
      value: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      max_value?: string | number;
      period_days?: number;
    }>;
    alert_query_config?: {
      properties: string[];
    };
  }>;
  toolName?: string;
  onUpdateLogicalOperator?: (
    brandId: number,
    thresholdIndex: number,
    operator: 'AND' | 'OR' | 'NONE' | 'ADD'
  ) => void;
}

export const PacingThresholdTable: React.FC<PacingThresholdTableProps> = ({
  brandConfig,
  onUpdateThreshold,
  onAddThresholdRow,
  onRemoveThresholdRow,
  onReorderThresholds,
  onClearAllThresholds,
  isLoadingInitialData,
  alertTypes,
  toolName,
  onUpdateLogicalOperator,
}) => {
  // Fiscal data
  const [brandFiscalData, setBrandFiscalData] = useState<any[]>([]);
  useEffect(() => {
    if (brandConfig.brandId) {
      apiClient
        .get(`/fiscal/v1/fiscal_calendar/brand/${brandConfig.brandId}`)
        .then((response: any) => setBrandFiscalData(response.data?.response || []))
        .catch(() => setBrandFiscalData([]));
    } else {
      setBrandFiscalData([]);
    }
  }, [brandConfig.brandId]);

  // Date presets
  const [datePresetInfo, setDatePresetInfo] = useState<any[]>([]);
  const [datePresetVar, setDatePresetVar] = useState<Record<string, string>>({});
  useEffect(() => {
    apiClient
      .get('/user/v1/user/datepreset')
      .then((response: any) => {
        const presets = response.data?.response?.datePreset || [];
        const variables = response.data?.response?.variables || {};
        if (presets.length > 0) setDatePresetInfo(presets);
        if (Object.keys(variables).length > 0) setDatePresetVar(variables);
      })
      .catch((err: any) =>
        console.error('[PacingThresholdTable] Failed to fetch date presets', err)
      );
  }, []);

  // Pacing date range - fetch available date range for the brand
  const [pacingDateRange, setPacingDateRange] = useState<PacingDateRange | null>(null);
  const [loadingPacingDateRange, setLoadingPacingDateRange] = useState(false);

  useEffect(() => {
    const fetchPacingDateRange = async () => {
      if (!brandConfig.brandId || toolName !== 'Pacing') {
        setPacingDateRange(null);
        setLoadingPacingDateRange(false);
        return;
      }

      setLoadingPacingDateRange(true);
      try {
        const response = await apiClient.get(
          `${DMG_ALERT_API_BASE_URL}/api/v1/pacing/brands/${brandConfig.brandId}/date-range`
        );
        const data = response.data;
        setPacingDateRange(data);

        if (!data.has_data) {
          Toastify(
            'warning',
            `No pacing data available for brand ${brandConfig.brandName}. Date range selection may be limited.`
          );
        }
      } catch (error: any) {
        console.error('Error fetching pacing date range:', error);
        setPacingDateRange(null);

        if (error.response?.status === 503) {
          Toastify('error', 'BigQuery service is currently unavailable. Please try again later.');
        }
      } finally {
        setLoadingPacingDateRange(false);
      }
    };

    fetchPacingDateRange();
  }, [brandConfig.brandId, brandConfig.brandName, toolName]);

  // Helper to validate if a date is within the pacing date range
  const validateDateAgainstPacingRange = useCallback(
    (startDate: Date, endDate: Date): { isValid: boolean; warning: string | null } => {
      if (
        !pacingDateRange ||
        !pacingDateRange.has_data ||
        !pacingDateRange.min_date ||
        !pacingDateRange.max_date
      ) {
        return { isValid: true, warning: null };
      }

      const minDate = new Date(pacingDateRange.min_date);
      const maxDate = new Date(pacingDateRange.max_date);
      const warnings: string[] = [];

      // Reset time parts for accurate date comparison
      const startDateOnly = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());

      if (startDateOnly < minDateOnly) {
        warnings.push(
          `Available data from ${formatDateToMMDDYYYY(pacingDateRange.min_date)} to ${formatDateToMMDDYYYY(pacingDateRange.max_date)}`
        );
      }

      if (endDateOnly > maxDateOnly) {
        warnings.push(
          `Available data from ${formatDateToMMDDYYYY(pacingDateRange.min_date)} to ${formatDateToMMDDYYYY(pacingDateRange.max_date)}`
        );
      }

      return {
        isValid: warnings.length === 0,
        warning: warnings.length > 0 ? warnings.join('. ') : null,
      };
    },
    [pacingDateRange]
  );

  // Memoize extracted dates for each threshold to prevent creating new Date objects on every render
  // This is critical because DatePickerDateRange's useEffect syncs range from initialStartDate/initialEndDate
  // and if we pass new Date objects on each render, it resets the user's selection
  const memoizedThresholdDates = useMemo(() => {
    const dateCache: Record<string, { startDate: Date; endDate: Date } | null> = {};
    brandConfig.thresholds.forEach(threshold => {
      // Use threshold.endDate as the cache key since that's where the dates are stored
      const cacheKey = `${threshold.id}-${threshold.endDate || 'empty'}`;
      dateCache[cacheKey] = extractDatesFromThreshold(threshold.endDate);
    });
    return dateCache;
  }, [brandConfig.thresholds]);

  // Helper to get memoized dates for a threshold
  const getMemoizedDates = useCallback(
    (threshold: BrandThreshold) => {
      const cacheKey = `${threshold.id}-${threshold.endDate || 'empty'}`;
      return memoizedThresholdDates[cacheKey];
    },
    [memoizedThresholdDates]
  );

  // Operator dropdown tooltip tracking (unchanged)
  const [openOperatorDropdown, setOpenOperatorDropdown] = useState<string | null>(null);
  useEffect(() => {
    const operatorTooltips: Record<string, string> = {
      '+': 'upper bound',
      '-': 'lower bound',
      '+-': 'lower upper bound',
      '+=': 'greater than or equal',
      '-=': 'less than or equal',
      '+-=': 'greater or less than or equal',
    };

    let observerInstance: MutationObserver | null = null;
    let isObserverPaused = false;

    const addTooltipsToOperatorOptions = () => {
      const optionButtons = document.querySelectorAll('[role="option"]');
      optionButtons.forEach(button => {
        if (button.hasAttribute('data-operator-tooltip-added')) return;

        const labelElement = button.querySelector('span');
        if (!labelElement) return;

        const optionLabel = labelElement.textContent?.trim();
        const labelToValue: Record<string, string> = {
          '>': '+',
          '<': '-',
          '><': '+-',
          '>=': '+=',
          '<=': '-=',
          '>=<=': '+-=',
        };
        const operatorValue = labelToValue[optionLabel || ''];
        if (!operatorValue || !operatorTooltips[operatorValue]) return;

        const tooltipText = operatorTooltips[operatorValue];
        button.setAttribute('data-operator-tooltip-added', 'true');
        button.setAttribute('data-operator-value', operatorValue);
        button.setAttribute('data-tooltip-text', tooltipText);

        let tooltipElement: HTMLDivElement | null = null;

        const showTooltip = () => {
          if (tooltipElement && document.body.contains(tooltipElement)) {
            tooltipElement.remove();
            tooltipElement = null;
          }
          const existingTooltip = document.querySelector(
            `[data-operator-tooltip="${operatorValue}"]`
          );
          if (existingTooltip) existingTooltip.remove();

          const tooltip = document.createElement('div');
          tooltip.setAttribute('data-operator-tooltip', operatorValue);
          tooltip.className =
            'z-[9999] px-2 py-1 text-xs text-white bg-black rounded shadow-lg pointer-events-none';
          tooltip.textContent = tooltipText;

          const rect = button.getBoundingClientRect();
          tooltip.style.position = 'fixed';
          tooltip.style.left = `${rect.right + 8}px`;
          tooltip.style.top = `${rect.top + rect.height / 2}px`;
          tooltip.style.transform = 'translateY(-50%)';

          if (observerInstance && !isObserverPaused) {
            isObserverPaused = true;
            observerInstance.disconnect();
          }

          document.body.appendChild(tooltip);
          tooltipElement = tooltip;

          setTimeout(() => {
            if (observerInstance && isObserverPaused) {
              observerInstance.observe(document.body, { childList: true, subtree: true });
              isObserverPaused = false;
            }
          }, 100);
        };

        const hideTooltip = () => {
          if (tooltipElement) {
            if (document.body.contains(tooltipElement)) tooltipElement.remove();
            tooltipElement = null;
          } else {
            const tooltip = document.querySelector(`[data-operator-tooltip="${operatorValue}"]`);
            if (tooltip) tooltip.remove();
          }
        };

        button.addEventListener('mouseenter', showTooltip as EventListener);
        button.addEventListener('mouseleave', hideTooltip);
        button.addEventListener('click', hideTooltip);
      });
    };

    observerInstance = new MutationObserver(mutations => {
      if (isObserverPaused) return;

      const listboxAdded = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(
          node =>
            node instanceof Element &&
            ((node.querySelector && node.querySelector('[role="listbox"]')) ||
              node.getAttribute('role') === 'listbox')
        )
      );

      const listboxRemoved = mutations.some(mutation =>
        Array.from(mutation.removedNodes).some(
          node =>
            node instanceof Element &&
            ((node.querySelector && node.querySelector('[role="listbox"]')) ||
              node.getAttribute('role') === 'listbox')
        )
      );

      const hasTooltipMutations = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(
          node => node instanceof Element && node.hasAttribute('data-operator-tooltip')
        )
      );

      if (hasTooltipMutations && !listboxRemoved) return;

      if (listboxAdded) {
        const listbox = document.querySelector('[role="listbox"]');
        if (listbox) {
          let thresholdId = '';
          let parent = listbox.parentElement;
          let depth = 0;
          while (parent && depth < 10) {
            const thresholdElement = parent.querySelector('[data-threshold-id]');
            if (thresholdElement) {
              thresholdId = thresholdElement.getAttribute('data-threshold-id') || '';
              break;
            }
            parent = parent.parentElement;
            depth++;
          }
          if (thresholdId) {
            setOpenOperatorDropdown(thresholdId);
            setTimeout(() => addTooltipsToOperatorOptions(), 150);
          }
        }
      } else if (listboxRemoved) {
        setTimeout(() => {
          const listbox = document.querySelector('[role="listbox"]');
          if (!listbox) {
            document.querySelectorAll('[data-operator-tooltip]').forEach(t => t.remove());
            setOpenOperatorDropdown(null);
          }
        }, 100);
      }
    });

    observerInstance.observe(document.body, { childList: true, subtree: true });

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const operatorSelect = target.closest('[data-operator-select]');
      if (operatorSelect) {
        const thresholdId = operatorSelect.getAttribute('data-threshold-id') || '';
        setTimeout(() => {
          const listbox = document.querySelector('[role="listbox"]');
          if (listbox) {
            setOpenOperatorDropdown(thresholdId);
            addTooltipsToOperatorOptions();
          } else {
            document.querySelectorAll('[data-operator-tooltip]').forEach(t => t.remove());
            setOpenOperatorDropdown(null);
          }
        }, 100);
      } else {
        document.querySelectorAll('[data-operator-tooltip]').forEach(t => t.remove());
        setOpenOperatorDropdown(null);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      if (observerInstance) observerInstance.disconnect();
      document.removeEventListener('click', handleClick);
      document.querySelectorAll('[data-operator-tooltip]').forEach(t => t.remove());
    };
  }, []);

  // ========= KEEP PREVIOUS APIs FOR METRIC TYPE CHECKING =========
  const [loadingStandardMetrics, setLoadingStandardMetrics] = useState(false);
  const [loadingCustomMetrics, setLoadingCustomMetrics] = useState(false);

  const [standardMetrics, setStandardMetrics] = useState<
    Array<{ key: string; display_name: string; description: string }>
  >([]);
  const [customMetrics, setCustomMetrics] = useState<
    Array<{
      id: number;
      name: string;
      latex: string;
      js_expr: string;
      metadata: string;
      created_at: string;
    }>
  >([]);

  useEffect(() => {
    const fetchStandardMetrics = async () => {
      setLoadingStandardMetrics(true);
      try {
        const metrics = await dmgAlertApiService.getStandardMetrics();
        setStandardMetrics(metrics);
      } catch (e) {
        console.error('Error fetching standard metrics:', e);
        setStandardMetrics([]);
      } finally {
        setLoadingStandardMetrics(false);
      }
    };
    fetchStandardMetrics();
  }, []);

  useEffect(() => {
    const fetchCustomMetrics = async () => {
      if (!brandConfig.brandId) {
        setCustomMetrics([]);
        setLoadingCustomMetrics(false);
        return;
      }
      setLoadingCustomMetrics(true);
      try {
        const metrics = await dmgAlertApiService.getCustomMetrics(brandConfig.brandId);
        setCustomMetrics(metrics);
      } catch (e) {
        console.error('Error fetching custom metrics:', e);
        setCustomMetrics([]);
      } finally {
        setLoadingCustomMetrics(false);
      }
    };
    fetchCustomMetrics();
  }, [brandConfig.brandId]);

  const getMetricMetadata = (metricName: string) => {
    const standardMetric = standardMetrics.find(m => m.key === metricName);
    if (standardMetric) return { ...standardMetric, id: null, category: 'standard' as const };
    const customMetric = customMetrics.find(m => m.name === metricName);
    if (customMetric) return { ...customMetric, category: 'custom' as const };
    return null;
  };

  const updateMetricWithMetadata = (
    brandId: number,
    thresholdIndex: number,
    metricName: string
  ) => {
    const metadata = getMetricMetadata(metricName);
    if (metadata) {
      const metricNameToStore =
        metadata.category === 'standard' ? (metadata as any).key : (metadata as any).name;

      onUpdateThreshold(brandId, thresholdIndex, 'metric', metricNameToStore);

      if ((metadata as any).id) {
        onUpdateThreshold(brandId, thresholdIndex, 'metricsId', (metadata as any).id);
      } else {
        onUpdateThreshold(brandId, thresholdIndex, 'metricsId', 0);
      }

      onUpdateThreshold(brandId, thresholdIndex, 'metricsCategory', metadata.category);
    } else {
      // fallback
      onUpdateThreshold(brandId, thresholdIndex, 'metric', metricName);
      onUpdateThreshold(brandId, thresholdIndex, 'metricsId', 0);
      onUpdateThreshold(brandId, thresholdIndex, 'metricsCategory', '');
    }
  };

  // ========= SHEET CONFIGS (ONLY used to constrain dropdown options) =========
  const [sheetConfigs, setSheetConfigs] = useState<SheetConfig[]>([]);
  const [loadingSheetConfigs, setLoadingSheetConfigs] = useState(false);

  useEffect(() => {
    const fetchSheetConfigs = async () => {
      if (!brandConfig.brandId) {
        setSheetConfigs([]);
        setLoadingSheetConfigs(false);
        return;
      }
      setLoadingSheetConfigs(true);
      try {
        const configs = await dmgAlertApiService.getSheetConfigs(brandConfig.brandId);
        setSheetConfigs(configs);
      } catch (e) {
        console.error('Error fetching sheet configs:', e);
        setSheetConfigs([]);
      } finally {
        setLoadingSheetConfigs(false);
      }
    };
    fetchSheetConfigs();
  }, [brandConfig.brandId]);

  const getSheetConfigById = (idStr: string) =>
    sheetConfigs.find(cfg => String(cfg.id) === String(idStr));

  const dimensionOptions = useMemo(() => {
    if (loadingSheetConfigs) return [];

    const options = sheetConfigs.map(cfg => {
      const label = getDimensionCombinationLabel(cfg.dimensions);
      return { label, value: String(cfg.id), title: label };
    });

    // Check if there's an "All Dimensions" option from sheet configs
    const hasAllDimension = sheetConfigs.some(cfg => isAllDimension(cfg.dimensions));

    // If no "All Dimensions" option exists in sheet configs, add one at the beginning
    if (!hasAllDimension) {
      options.unshift({
        label: 'All Dimensions',
        value: 'ALL_DIMENSION',
        title: 'All Dimensions',
      });
    }

    // Add "Select All" option at the beginning (excludes ALL_DIMENSION)
    // Only show if there are multiple dimensions to select
    const nonAllDimensions = sheetConfigs.filter(cfg => !isAllDimension(cfg.dimensions));
    if (nonAllDimensions.length > 1) {
      options.unshift({
        label: 'Select All',
        value: 'SELECT_ALL',
        title: 'Select All Dimensions',
      });
    }

    return options;
  }, [sheetConfigs, loadingSheetConfigs]);

  const getMetricsForSheetConfigId = (
    sheetConfigId: string
  ): Array<{ label: string; value: string }> => {
    // Special case: "ALL_DIMENSION" returns all metrics from all sheet configs
    if (sheetConfigId === 'ALL_DIMENSION') {
      // Collect all unique metrics from all sheet configs
      const allSheetConfigMetrics = new Set<string>();
      sheetConfigs.forEach(cfg => {
        (cfg.metrics || []).forEach(m => allSheetConfigMetrics.add(m.toLowerCase()));
      });

      const options: Array<{ label: string; value: string }> = [];

      // Add standard metrics that are present in any sheet config
      standardMetrics.forEach(m => {
        if (allSheetConfigMetrics.has(m.key.toLowerCase())) {
          options.push({ label: m.display_name, value: m.key });
        }
      });

      // Add custom metrics that are present in any sheet config
      customMetrics.forEach(m => {
        if (allSheetConfigMetrics.has(m.name.toLowerCase())) {
          options.push({ label: m.name, value: m.name });
        }
      });

      return options;
    }

    const cfg = getSheetConfigById(sheetConfigId);
    if (!cfg) return [];

    const allowed = new Set((cfg.metrics || []).map(m => m.toLowerCase()));
    const options: Array<{ label: string; value: string }> = [];

    standardMetrics.forEach(m => {
      if (allowed.has(m.key.toLowerCase())) options.push({ label: m.display_name, value: m.key });
    });

    customMetrics.forEach(m => {
      if (allowed.has(m.name.toLowerCase())) options.push({ label: m.name, value: m.name });
    });

    return options;
  };

  // Disabled state (wait for sheet configs + metric APIs)
  const isDisabled =
    isLoadingInitialData || loadingSheetConfigs || loadingStandardMetrics || loadingCustomMetrics;

  // Validation
  const [, setThresholdErrors] = useState<Record<string, string>>({});
  const maxValue = useMemo(
    () =>
      alertTypes?.find(at => at.display_name === toolName)?.default_threshold_config?.[0]
        ?.max_value !== undefined
        ? parseFloat(
            String(
              alertTypes.find(at => at.display_name === toolName)?.default_threshold_config?.[0]
                ?.max_value
            )
          )
        : undefined,
    [alertTypes, toolName]
  );

  useEffect(() => {
    const errors: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      const effectiveMax = threshold.pacingValueType === 'number' ? Infinity : maxValue;
      const error = validateThreshold(threshold.threshold, effectiveMax);
      if (error) errors[errorKey] = error;
    });

    setThresholdErrors(prev => {
      const hasChanges =
        Object.keys(errors).length !== Object.keys(prev).length ||
        Object.keys(errors).some(key => errors[key] !== prev[key]);
      return hasChanges ? errors : prev;
    });
  }, [brandConfig.thresholds, brandConfig.brandId, maxValue]);

  // Local typing state
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const initializedKeysRef = useRef<Set<string>>(new Set());

  // Version counter to force SingleSelect remount when SELECT_ALL is processed
  const [dimensionSelectVersion, setDimensionSelectVersion] = useState<Record<string, number>>({});

  // State for "All Dimension" warning dialog
  const [showAllDimensionWarning, setShowAllDimensionWarning] = useState(false);
  const [pendingAllDimensionChange, setPendingAllDimensionChange] = useState<{
    rowIndex: number;
    rowsToRemove: number;
  } | null>(null);

  // State for "Select All" warning dialog
  const [showSelectAllWarning, setShowSelectAllWarning] = useState(false);
  const [pendingSelectAllChange, setPendingSelectAllChange] = useState<{
    rowIndex: number;
    thresholdId: string;
    rowsToAdd: number;
    rowsToRemove: number;
  } | null>(null);

  // State to track date range warnings per threshold row
  const [dateRangeWarnings, setDateRangeWarnings] = useState<Record<string, string>>({});

  // Ref to track last toast time per threshold (for debouncing duplicate toasts)
  const lastToastTimeRef = useRef<Record<string, number>>({});

  // Validate existing threshold dates when pacing date range loads
  useEffect(() => {
    if (!pacingDateRange || !pacingDateRange.has_data || toolName !== 'Pacing') {
      // Clear all warnings if no pacing data or not pacing tool
      setDateRangeWarnings({});
      return;
    }

    const newWarnings: Record<string, string> = {};

    brandConfig.thresholds.forEach(threshold => {
      const dates = extractDatesFromThreshold(threshold.endDate);
      if (dates) {
        const validation = validateDateAgainstPacingRange(dates.startDate, dates.endDate);
        if (!validation.isValid && validation.warning) {
          newWarnings[threshold.id] = validation.warning;
        }
      }
    });

    setDateRangeWarnings(newWarnings);
  }, [pacingDateRange, brandConfig.thresholds, toolName, validateDateAgainstPacingRange]);

  useEffect(() => {
    const updatedValues: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      if (!initializedKeysRef.current.has(errorKey)) {
        if (threshold.threshold !== null && threshold.threshold !== undefined) {
          updatedValues[errorKey] = String(threshold.threshold);
          initializedKeysRef.current.add(errorKey);
        }
      }
    });
    if (Object.keys(updatedValues).length > 0) {
      setLocalInputValues(prev => ({ ...prev, ...updatedValues }));
    }
  }, [brandConfig.thresholds, brandConfig.brandId]);

  // Drag-to-fill state
  const [selectedCell, setSelectedCell] = useState<CellSelectionState | null>(null);
  const [dragFillState, setDragFillState] = useState<DragFillState>({
    isActive: false,
    sourceRowIndex: -1,
    sourceColumn: 'dimension',
    targetRowIndices: [],
  });
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mouseYRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragDirectionRef = useRef<'up' | 'down' | null>(null);

  // Auto-scroll hook
  const { startAutoScroll, stopAutoScroll } = useAutoScroll({
    scrollContainerRef,
    isDraggingRef,
    mouseYRef,
    dragDirectionRef,
  });

  // Track cell refs for overlay positioning
  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [dragOverlay, setDragOverlay] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Get cell ref key
  const getCellRefKey = (rowIndex: number, column: FillableColumn) => `${rowIndex}-${column}`;

  // Register cell ref
  const registerCellRef = useCallback(
    (rowIndex: number, column: FillableColumn, el: HTMLTableCellElement | null) => {
      const key = getCellRefKey(rowIndex, column);
      if (el) {
        cellRefs.current.set(key, el);
      } else {
        cellRefs.current.delete(key);
      }
    },
    []
  );

  // Update overlay position when drag state changes
  useEffect(() => {
    if (!dragFillState.isActive) {
      setDragOverlay(null);
      return;
    }

    const sourceKey = getCellRefKey(dragFillState.sourceRowIndex, dragFillState.sourceColumn);
    const sourceCell = cellRefs.current.get(sourceKey);

    if (!sourceCell || !tableRef.current) {
      setDragOverlay(null);
      return;
    }

    const tableRect = tableRef.current.getBoundingClientRect();
    const sourceRect = sourceCell.getBoundingClientRect();

    // If no target rows yet, just show overlay on source cell
    if (dragFillState.targetRowIndices.length === 0) {
      setDragOverlay({
        top: sourceRect.top - tableRect.top,
        left: sourceRect.left - tableRect.left,
        width: sourceRect.width,
        height: sourceRect.height,
      });
      return;
    }

    // Determine if dragging up or down
    const firstTargetIndex = dragFillState.targetRowIndices[0];
    const lastTargetIndex =
      dragFillState.targetRowIndices[dragFillState.targetRowIndices.length - 1];
    const isDraggingUp = firstTargetIndex < dragFillState.sourceRowIndex;

    if (isDraggingUp) {
      // Dragging upward: overlay spans from first target to source
      const firstTargetKey = getCellRefKey(firstTargetIndex, dragFillState.sourceColumn);
      const firstTargetCell = cellRefs.current.get(firstTargetKey);

      if (firstTargetCell) {
        const firstTargetRect = firstTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: firstTargetRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: sourceRect.bottom - firstTargetRect.top,
        });
      }
    } else {
      // Dragging downward: overlay spans from source to last target
      const lastTargetKey = getCellRefKey(lastTargetIndex, dragFillState.sourceColumn);
      const lastTargetCell = cellRefs.current.get(lastTargetKey);

      if (lastTargetCell) {
        const lastTargetRect = lastTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: sourceRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: lastTargetRect.bottom - sourceRect.top,
        });
      }
    }
  }, [dragFillState]);

  // Handle cell selection
  const handleCellClick = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedCell({ rowIndex, column });
    },
    []
  );

  // Clear selection when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(e.target as Node)) {
        setSelectedCell(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll is now handled by useAutoScroll hook

  // Start drag fill
  const handleFillHandleMouseDown = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      isDraggingRef.current = true;
      mouseYRef.current = e.clientY;
      dragDirectionRef.current = null;
      setDragFillState({
        isActive: true,
        sourceRowIndex: rowIndex,
        sourceColumn: column,
        targetRowIndices: [],
        mouseY: e.clientY,
      });
    },
    []
  );

  // Track drag fill movement (supports both upward and downward dragging)
  const handleCellMouseEnter = useCallback(
    (rowIndex: number, column: FillableColumn) => {
      if (!dragFillState.isActive || column !== dragFillState.sourceColumn) {
        return;
      }

      // If on source row, clear all targets
      if (rowIndex === dragFillState.sourceRowIndex) {
        setDragFillState(prev => ({ ...prev, targetRowIndices: [] }));
        return;
      }

      const indices: number[] = [];

      if (rowIndex > dragFillState.sourceRowIndex) {
        // Dragging downward: fill rows below source
        for (let i = dragFillState.sourceRowIndex + 1; i <= rowIndex; i++) {
          indices.push(i);
        }
      } else {
        // Dragging upward: fill rows above source
        for (let i = rowIndex; i < dragFillState.sourceRowIndex; i++) {
          indices.push(i);
        }
      }

      setDragFillState(prev => ({ ...prev, targetRowIndices: indices }));
    },
    [dragFillState.isActive, dragFillState.sourceColumn, dragFillState.sourceRowIndex]
  );

  // Apply fill values on mouse up
  const applyDragFill = useCallback(() => {
    if (!dragFillState.isActive || dragFillState.targetRowIndices.length === 0) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'dimension',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }

    const sourceThreshold = brandConfig.thresholds[dragFillState.sourceRowIndex];
    if (!sourceThreshold) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'dimension',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }

    // Track rows that couldn't be updated (for validation messages)
    let skippedRows = 0;
    let updatedRows = 0;

    // Copy the source value to all target rows
    dragFillState.targetRowIndices.forEach(targetIndex => {
      const targetThreshold = brandConfig.thresholds[targetIndex];

      switch (dragFillState.sourceColumn) {
        case 'dimension':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'dimension',
            sourceThreshold.dimension || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'dimensionId',
            sourceThreshold.dimensionId || 0
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'dimensionType',
            sourceThreshold.dimensionType || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'dimensionCategory',
            sourceThreshold.dimensionCategory || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'dimensionValue',
            sourceThreshold.dimensionValue || ''
          );
          // Clear metric when dimension changes (it may not be valid for new dimension)
          onUpdateThreshold(brandConfig.brandId, targetIndex, 'metric', '');
          updatedRows++;
          break;
        case 'metric': {
          // Check if target row has a dimension selected
          const targetHasDimension =
            targetThreshold &&
            findMatchingSheetConfigId(
              targetThreshold.dimension,
              targetThreshold.dimensionValue,
              sheetConfigs
            );

          if (!targetHasDimension) {
            // Skip this row - no dimension selected
            skippedRows++;
            break;
          }

          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'metric',
            sourceThreshold.metric || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'metricsId',
            sourceThreshold.metricsId || 0
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'metricsCategory',
            sourceThreshold.metricsCategory || ''
          );
          updatedRows++;
          break;
        }
        case 'operator':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'operator',
            sourceThreshold.operator || '+'
          );
          updatedRows++;
          break;
        case 'threshold': {
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'threshold',
            sourceThreshold.threshold || 0
          );
          // Update local input values for threshold
          if (targetThreshold) {
            const errorKey = getThresholdErrorKey(brandConfig.brandId, targetThreshold.id);
            setLocalInputValues(prev => ({
              ...prev,
              [errorKey]: String(sourceThreshold.threshold || 0),
            }));
          }
          updatedRows++;
          break;
        }
        case 'startDate':
        case 'endDate':
          // Copy both date fields together for date range
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'startDate',
            sourceThreshold.startDate || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'endDate',
            sourceThreshold.endDate || ''
          );
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'calendarType',
            sourceThreshold.calendarType || 'calendar'
          );
          // Copy comparisonDate for relative calendar type
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'comparisonDate',
            sourceThreshold.comparisonDate || ''
          );
          updatedRows++;
          break;
        case 'pacingValueType':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'pacingValueType',
            sourceThreshold.pacingValueType || 'percentage'
          );
          updatedRows++;
          break;
      }
    });

    // Show appropriate toast message based on results
    if (skippedRows > 0 && updatedRows === 0) {
      Toastify('error', `Please select a dimension first. Failed to update ${skippedRows} row(s).`);
    } else if (skippedRows > 0) {
      Toastify(
        'warning',
        `Filled ${updatedRows} row(s). Skipped ${skippedRows} row(s) - please select a dimension first.`
      );
    } else if (updatedRows > 0) {
      Toastify('success', `Filled ${updatedRows} row(s)`);
    }

    setDragFillState({
      isActive: false,
      sourceRowIndex: -1,
      sourceColumn: 'dimension',
      targetRowIndices: [],
      mouseY: undefined,
    });
    setSelectedCell(null);
  }, [dragFillState, brandConfig.thresholds, brandConfig.brandId, onUpdateThreshold, sheetConfigs]);

  // Calculate target rows based on mouse Y position
  const calculateTargetRowsFromMouseY = useCallback(
    (mouseY: number): number[] => {
      if (!tableRef.current || !dragFillState.isActive) {
        return [];
      }

      const tableRect = tableRef.current.getBoundingClientRect();
      const relativeY = mouseY - tableRect.top;

      // Find which row the mouse is over by checking cell positions
      let targetRowIndex = -1;
      for (let i = 0; i < brandConfig.thresholds.length; i++) {
        const cellKey = getCellRefKey(i, dragFillState.sourceColumn);
        const cell = cellRefs.current.get(cellKey);
        if (cell) {
          const cellRect = cell.getBoundingClientRect();
          const cellRelativeTop = cellRect.top - tableRect.top;
          const cellRelativeBottom = cellRect.bottom - tableRect.top;

          // Check if mouse is within this row's bounds
          if (relativeY >= cellRelativeTop && relativeY <= cellRelativeBottom) {
            targetRowIndex = i;
            break;
          }
          // If mouse is between rows, use the row we just passed
          if (relativeY < cellRelativeTop && i > 0) {
            targetRowIndex = i - 1;
            break;
          }
        }
      }

      // If we didn't find a row, check if we're past the last row
      if (targetRowIndex === -1 && brandConfig.thresholds.length > 0) {
        const lastIndex = brandConfig.thresholds.length - 1;
        const lastCellKey = getCellRefKey(lastIndex, dragFillState.sourceColumn);
        const lastCell = cellRefs.current.get(lastCellKey);
        if (lastCell) {
          const lastCellRect = lastCell.getBoundingClientRect();
          if (mouseY > lastCellRect.bottom) {
            targetRowIndex = lastIndex;
          }
        }
      }

      if (targetRowIndex === -1 || targetRowIndex === dragFillState.sourceRowIndex) {
        return [];
      }

      const indices: number[] = [];
      if (targetRowIndex > dragFillState.sourceRowIndex) {
        // Dragging downward: fill rows below source
        for (let i = dragFillState.sourceRowIndex + 1; i <= targetRowIndex; i++) {
          indices.push(i);
        }
      } else {
        // Dragging upward: fill rows above source
        for (let i = targetRowIndex; i < dragFillState.sourceRowIndex; i++) {
          indices.push(i);
        }
      }

      return indices;
    },
    [
      dragFillState.isActive,
      dragFillState.sourceRowIndex,
      dragFillState.sourceColumn,
      brandConfig.thresholds.length,
    ]
  );

  // Track mouse movement during drag for auto-scroll and overlay updates
  useEffect(() => {
    if (!dragFillState.isActive) {
      isDraggingRef.current = false;
      mouseYRef.current = null;
      stopAutoScroll();
      return;
    }

    let rafId: number | null = null;
    let hasMovedMouse = false; // Track if user has actually started dragging
    const initialMouseY = dragFillState.mouseY; // Store initial mouse position

    const updateOverlayFromMousePosition = () => {
      if (!dragFillState.isActive || mouseYRef.current === null) {
        return;
      }

      const targetIndices = calculateTargetRowsFromMouseY(mouseYRef.current);

      // Only update if the target indices changed to avoid unnecessary re-renders
      setDragFillState(prev => {
        if (
          prev.targetRowIndices.length !== targetIndices.length ||
          prev.targetRowIndices.some((idx, i) => idx !== targetIndices[i])
        ) {
          return { ...prev, targetRowIndices: targetIndices };
        }
        return prev;
      });

      rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseYRef.current = e.clientY;

      // Track drag direction based on movement from initial click position
      if (initialMouseY !== undefined) {
        if (e.clientY > initialMouseY + 5) {
          dragDirectionRef.current = 'down';
        } else if (e.clientY < initialMouseY - 5) {
          dragDirectionRef.current = 'up';
        }
      }

      // Only start auto-scroll after user has moved mouse significantly (at least 10px)
      // This prevents immediate scroll when clicking on last row's fill handle
      if (!hasMovedMouse && initialMouseY !== undefined) {
        const mouseDelta = Math.abs(e.clientY - initialMouseY);
        if (mouseDelta > 10) {
          hasMovedMouse = true;
          startAutoScroll();
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    // Don't start auto-scroll immediately - wait for mouse movement

    // Start the animation frame loop for smooth overlay updates
    rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      isDraggingRef.current = false;
      mouseYRef.current = null;
      dragDirectionRef.current = null;
      stopAutoScroll();
    };
  }, [
    dragFillState.isActive,
    dragFillState.mouseY,
    startAutoScroll,
    stopAutoScroll,
    calculateTargetRowsFromMouseY,
  ]);

  // Handle mouse up to apply drag fill
  useEffect(() => {
    const handleMouseUp = () => {
      if (dragFillState.isActive) {
        isDraggingRef.current = false;
        mouseYRef.current = null;
        dragDirectionRef.current = null;
        stopAutoScroll();
        applyDragFill();
      }
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragFillState.isActive, applyDragFill, stopAutoScroll]);

  // Helper to check if a cell is in the fill target range
  const isCellInFillRange = (rowIndex: number, column: FillableColumn) => {
    return (
      dragFillState.isActive &&
      dragFillState.sourceColumn === column &&
      dragFillState.targetRowIndices.includes(rowIndex)
    );
  };

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = brandConfig.thresholds.findIndex(t => t.id === active.id);
      const newIndex = brandConfig.thresholds.findIndex(t => t.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderThresholds(brandConfig.brandId, oldIndex, newIndex);
      }
    }
  };

  // Ensure at least one row
  useEffect(() => {
    if (brandConfig.thresholds.length === 0) {
      onAddThresholdRow(brandConfig.brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds.length]);

  // Fix: Auto-set startDate to 'custom_range' when endDate exists but startDate is undefined
  // This ensures DatePickerDateRange can display the dates even if preset info is missing
  useEffect(() => {
    brandConfig.thresholds.forEach((threshold, index) => {
      const hasEndDate = !!threshold.endDate && threshold.endDate.includes('|');
      const hasStartDate = !!threshold.startDate;

      if (hasEndDate && !hasStartDate) {
        onUpdateThreshold(brandConfig.brandId, index, 'startDate', 'custom_range');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds, brandConfig.brandId]);

  // Cleanup auto-scroll on unmount
  useEffect(() => {
    return () => {
      stopAutoScroll();
      isDraggingRef.current = false;
      mouseYRef.current = null;
    };
  }, [stopAutoScroll]);

  const handleRemoveRow = (thresholdIndex: number) => {
    onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);
  };

  const handleClearAll = () => {
    onClearAllThresholds(brandConfig.brandId);
  };

  // Handler for confirming "All Dimension" change
  const handleConfirmAllDimension = () => {
    if (!pendingAllDimensionChange) return;

    const { rowIndex } = pendingAllDimensionChange;

    // Set the dimension to ALL_DIMENSION
    onUpdateThreshold(brandConfig.brandId, rowIndex, 'dimension', 'ALL_DIMENSION');
    onUpdateThreshold(brandConfig.brandId, rowIndex, 'dimensionId', 0);
    onUpdateThreshold(brandConfig.brandId, rowIndex, 'dimensionType', 'All');
    onUpdateThreshold(brandConfig.brandId, rowIndex, 'dimensionCategory', 'standard');

    // Store "All Dimension" payload format for API
    const allDimensionPayload = [
      {
        name: 'All',
        type: 'All',
        value: [],
        category: 'standard',
        dimension_id: null,
      },
    ];
    onUpdateThreshold(
      brandConfig.brandId,
      rowIndex,
      'dimensionValue',
      JSON.stringify(allDimensionPayload)
    );
    // When "All Dimensions" is selected, default date range type to relative
    onUpdateThreshold(brandConfig.brandId, rowIndex, 'calendarType', 'relative');

    // Remove all rows that don't have ALL_DIMENSION selected
    const indicesToRemove = brandConfig.thresholds
      .map((t, idx) => ({ threshold: t, index: idx }))
      .filter(({ threshold, index }) => {
        if (index === rowIndex) return false; // Don't remove current row
        const resolvedId = findMatchingSheetConfigId(
          threshold.dimension,
          threshold.dimensionValue,
          sheetConfigs
        );
        return resolvedId !== 'ALL_DIMENSION';
      })
      .map(({ index }) => index)
      .sort((a, b) => b - a); // Sort descending to remove from end first

    // Remove rows from highest index to lowest to avoid index shifting issues
    indicesToRemove.forEach(idx => {
      onRemoveThresholdRow(brandConfig.brandId, idx);
    });

    // Close dialog and reset state
    setShowAllDimensionWarning(false);
    setPendingAllDimensionChange(null);
  };

  // Handler for canceling "All Dimension" change
  const handleCancelAllDimension = () => {
    setShowAllDimensionWarning(false);
    setPendingAllDimensionChange(null);
  };

  // Handler for confirming "Select All" change
  const handleConfirmSelectAll = () => {
    if (!pendingSelectAllChange) return;

    const { rowIndex, thresholdId } = pendingSelectAllChange;

    // Increment version to force SingleSelect remount
    setDimensionSelectVersion(prev => ({
      ...prev,
      [thresholdId]: (prev[thresholdId] || 0) + 1,
    }));

    // First, remove all rows that have ALL_DIMENSION selected
    const indicesToRemoveAllDim = brandConfig.thresholds
      .map((t, idx) => ({ threshold: t, index: idx }))
      .filter(({ threshold: th }) => {
        const resolvedId = findMatchingSheetConfigId(th.dimension, th.dimensionValue, sheetConfigs);
        return resolvedId === 'ALL_DIMENSION';
      })
      .map(({ index: idx }) => idx)
      .sort((a, b) => b - a); // Sort descending to remove from end first

    // Check if current row will be removed
    const willCurrentRowBeRemoved = indicesToRemoveAllDim.includes(rowIndex);

    // Remove rows from highest index to lowest to avoid index shifting issues
    indicesToRemoveAllDim.forEach(idx => {
      onRemoveThresholdRow(brandConfig.brandId, idx);
    });

    // Get all sheet configs that are NOT "ALL_DIMENSION"
    const nonAllDimensions = sheetConfigs.filter(cfg => !isAllDimension(cfg.dimensions));

    if (nonAllDimensions.length === 0) {
      setShowSelectAllWarning(false);
      setPendingSelectAllChange(null);
      return;
    }

    // Calculate the correct index for the current row after removals
    const rowsRemovedBeforeCurrent = indicesToRemoveAllDim.filter(idx => idx < rowIndex).length;
    const adjustedCurrentIndex = rowIndex - rowsRemovedBeforeCurrent;

    // If current row was removed, add it back first
    if (willCurrentRowBeRemoved) {
      onAddThresholdRow(brandConfig.brandId);
    }

    // Fill the current row with the first dimension
    const firstCfg = nonAllDimensions[0];
    const targetRowIndex = adjustedCurrentIndex;

    // Get remaining dimensions (n-1 rows to add)
    const remainingDimensions = nonAllDimensions.slice(1);

    // Fill current row first with the first dimension
    onUpdateThreshold(brandConfig.brandId, targetRowIndex, 'dimension', String(firstCfg.id));
    onUpdateThreshold(brandConfig.brandId, targetRowIndex, 'dimensionId', firstCfg.id);
    onUpdateThreshold(
      brandConfig.brandId,
      targetRowIndex,
      'dimensionType',
      firstCfg.type || 'sheet_config'
    );
    onUpdateThreshold(
      brandConfig.brandId,
      targetRowIndex,
      'dimensionCategory',
      firstCfg.type || 'sheet_config'
    );

    const firstPayload = convertDimensionsToPayloadFormat(firstCfg.dimensions);
    onUpdateThreshold(
      brandConfig.brandId,
      targetRowIndex,
      'dimensionValue',
      JSON.stringify(firstPayload)
    );

    // Now add n-1 rows for remaining dimensions
    remainingDimensions.forEach(() => {
      onAddThresholdRow(brandConfig.brandId);
    });

    // Configure the newly added rows after a delay
    setTimeout(() => {
      remainingDimensions.forEach((cfg, idx) => {
        const expectedFinalCount =
          brandConfig.thresholds.length -
          indicesToRemoveAllDim.length +
          (willCurrentRowBeRemoved ? 1 : 0) +
          remainingDimensions.length;

        const targetIndex = expectedFinalCount - remainingDimensions.length + idx;

        onUpdateThreshold(brandConfig.brandId, targetIndex, 'dimension', String(cfg.id));
        onUpdateThreshold(brandConfig.brandId, targetIndex, 'dimensionId', cfg.id);
        onUpdateThreshold(
          brandConfig.brandId,
          targetIndex,
          'dimensionType',
          cfg.type || 'sheet_config'
        );
        onUpdateThreshold(
          brandConfig.brandId,
          targetIndex,
          'dimensionCategory',
          cfg.type || 'sheet_config'
        );

        const payload = convertDimensionsToPayloadFormat(cfg.dimensions);
        onUpdateThreshold(
          brandConfig.brandId,
          targetIndex,
          'dimensionValue',
          JSON.stringify(payload)
        );
      });

      // Show toast notification for total rows (first row + added rows)
      const totalRowsAdded = 1 + remainingDimensions.length;
      Toastify(
        'success',
        `${totalRowsAdded} dimension row${totalRowsAdded === 1 ? '' : 's'} added`
      );
    }, 150);

    // Close dialog and reset state
    setShowSelectAllWarning(false);
    setPendingSelectAllChange(null);
  };

  // Handler for canceling "Select All" change
  const handleCancelSelectAll = () => {
    setShowSelectAllWarning(false);
    setPendingSelectAllChange(null);
  };

  return (
    <div className="mb-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Brand-Specific Thresholds ({brandConfig.brandName})
        </h3>

        {brandConfig.thresholds.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClearAll}
            disabled={isDisabled}
            className="text-xs"
          >
            Clear All
          </Button>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        className="overflow-x-auto w-full max-w-7xl relative"
        style={{ maxHeight: '600px', overflowY: 'auto' }}
      >
        {/* Drag Fill Selection Overlay */}
        {dragOverlay && dragFillState.isActive && (
          <div
            className="absolute pointer-events-none z-20 border-2 border-blue-500 bg-blue-500/10 rounded-sm"
            style={{
              top: dragOverlay.top,
              left: dragOverlay.left,
              width: dragOverlay.width,
              height: dragOverlay.height,
              transition: 'height 0.05s ease-out',
            }}
          >
            {/* Bottom edge indicator - only show when there are targets */}
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
            {/* Row count badge - only show when there are targets */}
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute -right-8 top-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-lg">
                {dragFillState.targetRowIndices.length}
              </div>
            )}
          </div>
        )}
        <table
          ref={tableRef}
          className="border-collapse border border-gray-300 w-full"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '3%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '31%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '7%' }} />
          </colgroup>

          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-1 py-2 text-center text-sm font-medium text-gray-700">
                <div className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3 text-gray-400"
                  >
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Dimension</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Metric</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex flex-col">
                  <span>Comparison Date Range</span>
                  {toolName === 'Pacing' && pacingDateRange && pacingDateRange.has_data && (
                    <span className="text-xs font-normal text-gray-500 mt-0.5">
                      Available: {formatDateToMMDDYYYY(pacingDateRange.min_date)} to{' '}
                      {formatDateToMMDDYYYY(pacingDateRange.max_date)}
                    </span>
                  )}
                  {toolName === 'Pacing' && loadingPacingDateRange && (
                    <span className="text-xs font-normal text-gray-400 mt-0.5">
                      Loading date range...
                    </span>
                  )}
                  {toolName === 'Pacing' && pacingDateRange && !pacingDateRange.has_data && (
                    <span className="text-xs font-normal text-yellow-600 mt-0.5">
                      No pacing data available
                    </span>
                  )}
                </div>
              </th>
              <th
                className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700"
                style={{ minWidth: '120px' }}
              >
                Operator
              </th>
              <th
                className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700"
                style={{ minWidth: '160px' }}
              >
                Value Type
              </th>
              <th
                className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700"
                style={{ width: '90px' }}
              >
                Threshold
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">
                Actions
              </th>
            </tr>
          </thead>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext
              items={brandConfig.thresholds.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {brandConfig.thresholds.map((threshold, index) => {
                  const originalIndex = index;
                  const isLastRow = index === brandConfig.thresholds.length - 1;

                  const prevThreshold = index > 0 ? brandConfig.thresholds[index - 1] : null;
                  const connToNext = checkConnectionToNext(threshold, isLastRow);
                  const connFromPrev = checkConnectionFromPrev(prevThreshold);

                  const { firstColBorderClass, middleColBorderClass, lastColBorderClass } =
                    getBorderClasses(connFromPrev, connToNext);

                  const logicalOperatorRow = !isLastRow && (
                    <tr>
                      <td
                        colSpan={6}
                        className="border-x border-gray-300 px-4 py-3 bg-gray-50/30"
                        style={{ borderTop: 'none', borderBottom: 'none' }}
                      >
                        <div className="flex items-center relative pl-8" style={{ height: '56px' }}>
                          {threshold.logicalOperator && threshold.logicalOperator !== 'NONE' && (
                            <div
                              className="absolute flex flex-col items-center"
                              style={{ height: '100%', width: '24px', left: '16px' }}
                            >
                              <div
                                className={`w-full h-1/2 border-l-2 border-b-2 rounded-bl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : 'border-orange-500'
                                }`}
                                style={{ borderBottomLeftRadius: '10px' }}
                              />
                              <div
                                className={`w-full h-1/2 border-l-2 border-t-2 rounded-tl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : 'border-orange-500'
                                }`}
                                style={{ borderTopLeftRadius: '10px' }}
                              />
                            </div>
                          )}

                          <div className="flex items-center gap-3 ml-8">
                            <SingleSelect
                              key={`${threshold.id}-logical-op`}
                              defaultValue={threshold.logicalOperator || 'NONE'}
                              disabled={isDisabled}
                              onChange={(value: string) => {
                                onUpdateLogicalOperator?.(
                                  brandConfig.brandId,
                                  originalIndex,
                                  value as 'AND' | 'OR' | 'NONE'
                                );
                              }}
                              options={[
                                { label: 'AND', value: 'AND' },
                                { label: 'OR', value: 'OR' },
                                { label: 'NONE', value: 'NONE' },
                              ]}
                              placeholder="Select Operator"
                              containerClassName="w-36"
                            />
                            {threshold.logicalOperator === 'AND' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-green-100 text-green-700 rounded-md shadow-sm">
                                Both threshold conditions must meet
                              </span>
                            )}
                            {threshold.logicalOperator === 'OR' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-md shadow-sm">
                                Any threshold condition meets
                              </span>
                            )}
                            {threshold.logicalOperator === 'NONE' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-md shadow-sm">
                                No connection
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );

                  return (
                    <SortableThresholdRow
                      key={threshold.id}
                      threshold={threshold}
                      index={index}
                      originalIndex={originalIndex}
                      isLastRow={isLastRow}
                      hasConnectionToNext={connToNext}
                      hasConnectionFromPrev={connFromPrev}
                      logicalOperatorRow={logicalOperatorRow}
                    >
                      {/* DIMENSION (sheet_config selection; still typed as sheet_config) */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'dimension', el)}
                        className={`${firstColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'dimension'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'dimension') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'dimension', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'dimension')}
                      >
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-full" data-threshold-id={threshold.id}>
                                <SingleSelect
                                  key={`${threshold.id}-dimension-sheet-${sheetConfigs.length}-${findMatchingSheetConfigId(threshold.dimension, threshold.dimensionValue, sheetConfigs)}-v${dimensionSelectVersion[threshold.id] || 0}`}
                                  defaultValue={findMatchingSheetConfigId(
                                    threshold.dimension,
                                    threshold.dimensionValue,
                                    sheetConfigs
                                  )}
                                  disabled={isDisabled}
                                  onChange={(value: string) => {
                                    if (!value || value.trim() === '') {
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimension',
                                        ''
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'metric',
                                        ''
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionId',
                                        0
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionType',
                                        ''
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionCategory',
                                        ''
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionValue',
                                        ''
                                      );
                                      return;
                                    }

                                    // Handle "SELECT_ALL" option - add rows for all dimensions (excluding ALL_DIMENSION)
                                    if (value === 'SELECT_ALL') {
                                      // Calculate rows to be removed (ALL_DIMENSION rows)
                                      const indicesToRemoveAllDim = brandConfig.thresholds
                                        .map((t, idx) => ({ threshold: t, index: idx }))
                                        .filter(({ threshold: th }) => {
                                          const resolvedId = findMatchingSheetConfigId(
                                            th.dimension,
                                            th.dimensionValue,
                                            sheetConfigs
                                          );
                                          return resolvedId === 'ALL_DIMENSION';
                                        })
                                        .map(({ index: idx }) => idx);

                                      // Get all sheet configs that are NOT "ALL_DIMENSION"
                                      const nonAllDimensions = sheetConfigs.filter(
                                        cfg => !isAllDimension(cfg.dimensions)
                                      );

                                      // Show confirmation dialog
                                      setPendingSelectAllChange({
                                        rowIndex: originalIndex,
                                        thresholdId: threshold.id,
                                        rowsToAdd: nonAllDimensions.length,
                                        rowsToRemove: indicesToRemoveAllDim.length,
                                      });
                                      setShowSelectAllWarning(true);

                                      // Increment version to reset the dropdown (don't show SELECT_ALL as selected)
                                      setDimensionSelectVersion(prev => ({
                                        ...prev,
                                        [threshold.id]: (prev[threshold.id] || 0) + 1,
                                      }));

                                      return;
                                    }

                                    // Handle synthetic "ALL_DIMENSION" option
                                    if (value === 'ALL_DIMENSION') {
                                      // Calculate rows that would be removed
                                      const indicesToRemove = brandConfig.thresholds
                                        .map((t, idx) => ({ threshold: t, index: idx }))
                                        .filter(({ threshold, index }) => {
                                          if (index === originalIndex) return false; // Don't remove current row
                                          const resolvedId = findMatchingSheetConfigId(
                                            threshold.dimension,
                                            threshold.dimensionValue,
                                            sheetConfigs
                                          );
                                          return resolvedId !== 'ALL_DIMENSION';
                                        })
                                        .map(({ index }) => index);

                                      // Show warning dialog if there are rows to be removed
                                      if (indicesToRemove.length > 0) {
                                        setPendingAllDimensionChange({
                                          rowIndex: originalIndex,
                                          rowsToRemove: indicesToRemove.length,
                                        });
                                        setShowAllDimensionWarning(true);
                                        // Increment version to reset the dropdown (don't show ALL_DIMENSION as selected yet)
                                        setDimensionSelectVersion(prev => ({
                                          ...prev,
                                          [threshold.id]: (prev[threshold.id] || 0) + 1,
                                        }));
                                        return;
                                      }

                                      // No rows to remove, proceed directly
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimension',
                                        'ALL_DIMENSION'
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionId',
                                        0
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionType',
                                        'All'
                                      );
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionCategory',
                                        'standard'
                                      );
                                      // Store "All Dimension" payload format for API
                                      const allDimensionPayload = [
                                        {
                                          name: 'All',
                                          type: 'All',
                                          value: [],
                                          category: 'standard',
                                          dimension_id: null,
                                        },
                                      ];
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'dimensionValue',
                                        JSON.stringify(allDimensionPayload)
                                      );
                                      // When "All Dimensions" is selected, default date range type to relative
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'calendarType',
                                        'relative'
                                      );

                                      return;
                                    }

                                    const cfg = getSheetConfigById(value);
                                    if (!cfg) return;

                                    // Store sheet config selection + payload
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'dimension',
                                      value
                                    ); // sheetConfigId string
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'dimensionId',
                                      cfg.id
                                    );
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'dimensionType',
                                      cfg.type || 'sheet_config'
                                    );
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'dimensionCategory',
                                      cfg.type || 'sheet_config'
                                    );

                                    const payload = convertDimensionsToPayloadFormat(
                                      cfg.dimensions
                                    );
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'dimensionValue',
                                      JSON.stringify(payload)
                                    );

                                    // Clear invalid metric when dimension changes
                                    if (threshold.metric) {
                                      const allowed = new Set(
                                        (cfg.metrics || []).map(m => m.toLowerCase())
                                      );
                                      if (!allowed.has(threshold.metric.toLowerCase())) {
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          originalIndex,
                                          'metric',
                                          ''
                                        );
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          originalIndex,
                                          'metricsId',
                                          0
                                        );
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          originalIndex,
                                          'metricsCategory',
                                          ''
                                        );
                                      }
                                    }

                                    // Remove all rows that have ALL_DIMENSION selected (when a specific dimension is chosen)
                                    const indicesToRemoveAllDim = brandConfig.thresholds
                                      .map((t, idx) => ({ threshold: t, index: idx }))
                                      .filter(({ threshold: th, index }) => {
                                        if (index === originalIndex) return false; // Don't remove current row
                                        const resolvedId = findMatchingSheetConfigId(
                                          th.dimension,
                                          th.dimensionValue,
                                          sheetConfigs
                                        );
                                        return resolvedId === 'ALL_DIMENSION';
                                      })
                                      .map(({ index }) => index)
                                      .sort((a, b) => b - a); // Sort descending to remove from end first

                                    // Remove rows from highest index to lowest to avoid index shifting issues
                                    indicesToRemoveAllDim.forEach(idx => {
                                      onRemoveThresholdRow(brandConfig.brandId, idx);
                                    });
                                  }}
                                  options={dimensionOptions}
                                  placeholder="Select Dimension"
                                  containerClassName="w-full"
                                />
                              </div>
                            </TooltipTrigger>

                            {threshold.dimension && (
                              <TooltipContent
                                side="top"
                                className="max-w-md text-xs whitespace-pre-wrap"
                              >
                                {(() => {
                                  const resolvedId = findMatchingSheetConfigId(
                                    threshold.dimension,
                                    threshold.dimensionValue,
                                    sheetConfigs
                                  );
                                  if (resolvedId === 'ALL_DIMENSION') {
                                    return 'All Dimensions';
                                  }
                                  const cfg = getSheetConfigById(resolvedId);
                                  return cfg
                                    ? getDimensionCombinationLabel(cfg.dimensions)
                                    : threshold.dimension;
                                })()}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        {/* Fill Handle for Dimension */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'dimension' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'dimension', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* METRIC (filtered by sheet config; stored with metadata via APIs) */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'metric', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'metric'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'metric') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'metric', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'metric')}
                      >
                        {(() => {
                          const resolvedSheetConfigId = findMatchingSheetConfigId(
                            threshold.dimension,
                            threshold.dimensionValue,
                            sheetConfigs
                          );
                          const hasValidDimension = !!resolvedSheetConfigId;
                          return (
                            <SingleSelect
                              key={`${threshold.id}-metric-${threshold.metric || 'empty'}-${resolvedSheetConfigId || 'nodim'}`}
                              defaultValue={threshold.metric || ''}
                              disabled={isDisabled || !hasValidDimension}
                              onChange={(value: string) => {
                                if (!value || value.trim() === '') {
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'metric',
                                    ''
                                  );
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'metricsId',
                                    0
                                  );
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'metricsCategory',
                                    ''
                                  );
                                  return;
                                }
                                updateMetricWithMetadata(brandConfig.brandId, originalIndex, value);
                              }}
                              options={
                                hasValidDimension
                                  ? getMetricsForSheetConfigId(resolvedSheetConfigId)
                                  : []
                              }
                              placeholder={
                                !hasValidDimension ? 'Select dimension first' : 'Select Metric'
                              }
                              containerClassName="w-full"
                            />
                          );
                        })()}
                        {/* Fill Handle for Metric */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'metric' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'metric', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* DATE RANGE */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'startDate', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 overflow-hidden ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'startDate'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'startDate') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'startDate', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'startDate')}
                      >
                        <div className="flex items-center gap-2 min-w-0 overflow-hidden [&>*]:min-w-0 [&_button]:max-w-full [&_button]:truncate [&_[role='button']]:max-w-full [&_[role='button']]:truncate">
                          <DatePickerDateRange
                            key={`${brandConfig.brandId}-${threshold.id}-datepreset-${threshold.startDate || 'empty'}-${threshold.endDate || 'nodates'}-${threshold.calendarType || 'default'}-${threshold.comparisonDate || 'norel'}`}
                            brandId={`${brandConfig.brandId}-${threshold.id}`}
                            initialStartDate={getMemoizedDates(threshold)?.startDate}
                            initialEndDate={getMemoizedDates(threshold)?.endDate}
                            selectedDatePreset={
                              threshold.startDate
                                ? findPresetByValue(datePresetInfo, threshold.startDate)?.label
                                : undefined
                            }
                            datePresetInfo={datePresetInfo}
                            datePresetVar={datePresetVar}
                            defaultDatePreset={
                              threshold.startDate
                                ? findPresetByValue(datePresetInfo, threshold.startDate)?.label
                                : undefined
                            }
                            initalLoad={!!(threshold.startDate || getMemoizedDates(threshold))}
                            presetOnly={false}
                            fiscal={true}
                            fiscalData={brandFiscalData}
                            yearType={threshold.calendarType || 'calendar'}
                            showRelative={true}
                            selectedRelativeOption={
                              threshold.calendarType === 'relative'
                                ? threshold.comparisonDate || undefined
                                : undefined
                            }
                            onRelativeSelect={(
                              optionId: string,
                              _label: string,
                              start: Date,
                              end: Date
                            ) => {
                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'calendarType',
                                'relative'
                              );
                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'comparisonDate',
                                optionId
                              );
                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'startDate',
                                optionId
                              );
                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'endDate',
                                `${start.toISOString()}|${end.toISOString()}`
                              );
                              setDateRangeWarnings(prev => {
                                const next = { ...prev };
                                delete next[threshold.id];
                                return next;
                              });
                            }}
                            onYearTypeChange={(year: string) => {
                              if (year === 'fiscal') {
                                if (brandFiscalData && brandFiscalData.length > 0) {
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    originalIndex,
                                    'calendarType',
                                    'fiscal'
                                  );
                                } else {
                                  Toastify(
                                    'info',
                                    'Fiscal calendar does not exist for this brand.<br /><b><a target="_blank" href="https://forms.clickup.com/9011066952/f/8chky28-11731/7B9KQQ17LUQBPC721A"><u>Click here</u> to upload the calendar.</a></b>',
                                    'top-right'
                                  );
                                }
                              } else if (year === 'relative') {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'calendarType',
                                  'relative'
                                );
                              } else {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'calendarType',
                                  'calendar'
                                );
                              }
                            }}
                            onApply={(
                              calendarStart: Date,
                              calendarEnd: Date,
                              label: string,
                              fiscalStart?: Date,
                              fiscalEnd?: Date
                            ) => {
                              const preset = datePresetInfo.find((p: any) => p.label === label);
                              const isFiscal = threshold.calendarType === 'fiscal';

                              const startDate =
                                isFiscal && fiscalStart ? fiscalStart : calendarStart;
                              const endDate = isFiscal && fiscalEnd ? fiscalEnd : calendarEnd;

                              // Validate date range against pacing data availability (only for Pacing tool)
                              if (toolName === 'Pacing') {
                                const validation = validateDateAgainstPacingRange(
                                  startDate,
                                  endDate
                                );
                                if (!validation.isValid && validation.warning) {
                                  // Debounce toast: only show if 500ms has passed since last toast for this threshold
                                  const now = Date.now();
                                  const lastToastTime = lastToastTimeRef.current[threshold.id] || 0;
                                  if (now - lastToastTime > 500) {
                                    lastToastTimeRef.current[threshold.id] = now;
                                    Toastify('warning', validation.warning);
                                  }
                                  // Store warning for this threshold row
                                  setDateRangeWarnings(prev => ({
                                    ...prev,
                                    [threshold.id]: validation.warning!,
                                  }));
                                } else {
                                  // Clear warning for this threshold if dates are valid
                                  setDateRangeWarnings(prev => {
                                    const next = { ...prev };
                                    delete next[threshold.id];
                                    return next;
                                  });
                                }
                              }

                              // Clear relative fields when applying calendar/fiscal date selection
                              if (threshold.calendarType !== 'relative') {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'comparisonDate',
                                  ''
                                );
                              }

                              if (preset) {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'startDate',
                                  getPresetApiValue(preset)
                                );
                              } else {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'startDate',
                                  'custom_range'
                                );
                              }

                              onUpdateThreshold(
                                brandConfig.brandId,
                                originalIndex,
                                'endDate',
                                `${startDate.toISOString()}|${endDate.toISOString()}`
                              );
                            }}
                          />
                          {/* Date Range Warning Indicator */}
                          {dateRangeWarnings[threshold.id] && toolName === 'Pacing' && (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex-shrink-0">
                                    <Warning className="w-4 h-4 text-yellow-500" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs" side="top">
                                  {dateRangeWarnings[threshold.id]}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {/* Relative Date Range Info Indicator */}
                          {threshold.calendarType === 'relative' && threshold.comparisonDate && (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex-shrink-0 cursor-help">
                                    <InfoCircle className="w-4 h-4" style={{ color: '#334238' }} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs max-w-xs" side="top">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="font-medium">
                                      {threshold.comparisonDate
                                        .split('_')
                                        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                                        .join(' ')}
                                    </span>
                                    {getMemoizedDates(threshold) && (
                                      <span className="text-gray-400">
                                        {getMemoizedDates(threshold)!.startDate.toLocaleDateString(
                                          'en-US',
                                          { month: '2-digit', day: '2-digit', year: 'numeric' }
                                        )}{' '}
                                        –{' '}
                                        {getMemoizedDates(threshold)!.endDate.toLocaleDateString(
                                          'en-US',
                                          { month: '2-digit', day: '2-digit', year: 'numeric' }
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        {/* Fill Handle for Date Range */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'startDate' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'startDate', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* OPERATOR (unchanged) */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'operator', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'operator'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'operator') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        style={{ minWidth: '120px' }}
                        onClick={e => handleCellClick(originalIndex, 'operator', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'operator')}
                      >
                        <TooltipProvider delayDuration={100}>
                          <Tooltip open={openOperatorDropdown === threshold.id ? false : undefined}>
                            <TooltipTrigger asChild>
                              <div
                                className="w-full overflow-hidden"
                                data-threshold-id={threshold.id}
                                data-operator-select="true"
                              >
                                <SingleSelect
                                  key={`${threshold.id}-operator-${threshold.operator}`}
                                  defaultValue={threshold.operator || '+'}
                                  disabled={isDisabled}
                                  onChange={(value: string) => {
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      originalIndex,
                                      'operator',
                                      value as
                                        | '<'
                                        | '>'
                                        | '<='
                                        | '>='
                                        | '=='
                                        | '!='
                                        | '+'
                                        | '-'
                                        | '+-'
                                        | '+='
                                        | '-='
                                        | '+-='
                                    );
                                  }}
                                  options={[
                                    { label: '>', value: '+' },
                                    { label: '>=', value: '+=' },
                                    { label: '<', value: '-' },
                                    { label: '<=', value: '-=' },
                                    { label: '><', value: '+-' },
                                    { label: '>=<=', value: '+-=' },
                                  ]}
                                  placeholder="Select Operator"
                                  containerClassName="w-full"
                                  clearIconClassName="hidden"
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs" side="top">
                              {threshold.operator === '+' && 'upper bound'}
                              {threshold.operator === '+=' && 'greater than or equal'}
                              {threshold.operator === '-' && 'lower bound'}
                              {threshold.operator === '-=' && 'less than or equal'}
                              {threshold.operator === '+-' && 'lower upper bound'}
                              {threshold.operator === '+-=' && 'greater or less than or equal'}
                              {!threshold.operator && 'Select operator'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {/* Fill Handle for Operator */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'operator' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'operator', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* PACING VALUE TYPE */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'pacingValueType', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'pacingValueType'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'pacingValueType') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        style={{ minWidth: '160px' }}
                        onClick={e => handleCellClick(originalIndex, 'pacingValueType', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'pacingValueType')}
                      >
                        <SingleSelect
                          key={`${threshold.id}-pacingValueType-${threshold.pacingValueType || 'percentage'}`}
                          defaultValue={threshold.pacingValueType || 'percentage'}
                          disabled={isDisabled}
                          onChange={(value: string) => {
                            onUpdateThreshold(
                              brandConfig.brandId,
                              originalIndex,
                              'pacingValueType',
                              value as 'percentage' | 'number'
                            );
                          }}
                          options={[
                            { label: 'Percentage', value: 'percentage' },
                            { label: 'Number', value: 'number' },
                          ]}
                          placeholder="Select Type"
                          containerClassName="w-full"
                        />
                        {/* Fill Handle for Pacing Value Type */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'pacingValueType' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'pacingValueType', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* THRESHOLD */}
                      <td
                        ref={el => registerCellRef(originalIndex, 'threshold', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'threshold'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'threshold') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        style={{ width: '90px' }}
                        onClick={e => handleCellClick(originalIndex, 'threshold', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'threshold')}
                      >
                        {(() => {
                          const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
                          const localValue =
                            localInputValues[errorKey] !== undefined
                              ? localInputValues[errorKey]
                              : threshold.threshold !== null && threshold.threshold !== undefined
                                ? String(threshold.threshold)
                                : '';

                          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            const inputValue = e.target.value;
                            initializedKeysRef.current.add(errorKey);
                            setLocalInputValues(prev => ({ ...prev, [errorKey]: inputValue }));

                            if (inputValue !== '') {
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'threshold',
                                  numValue
                                );
                              }
                            }
                          };

                          const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                            const inputValue = e.target.value;
                            const numValue = inputValue === '' ? 0 : parseFloat(inputValue);

                            if (inputValue === '') {
                              onUpdateThreshold(brandConfig.brandId, originalIndex, 'threshold', 0);
                              setLocalInputValues(prev => ({ ...prev, [errorKey]: '0' }));
                            } else if (!isNaN(numValue)) {
                              setLocalInputValues(prev => ({
                                ...prev,
                                [errorKey]: String(numValue),
                              }));
                            }
                            const effectiveMax = threshold.pacingValueType === 'number' ? Infinity : maxValue;
                            const validationError = validateThreshold(numValue, effectiveMax);
                            if (validationError) {
                              Toastify('error', validationError);
                              setThresholdErrors(prev => ({
                                ...prev,
                                [errorKey]: validationError,
                              }));
                            } else {
                              setThresholdErrors(prev => {
                                const next = { ...prev };
                                delete next[errorKey];
                                return next;
                              });
                            }
                          };

                          const isPercentage =
                            !threshold.pacingValueType ||
                            threshold.pacingValueType === 'percentage';
                          return (
                            <div className="w-full relative">
                              <Input
                                type="number"
                                value={localValue}
                                disabled={isDisabled}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Value"
                                step="any"
                                className="w-full"
                                style={isPercentage ? { paddingRight: '24px' } : undefined}
                              />
                              {isPercentage && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-sm">
                                  %
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {/* Fill Handle for Threshold */}
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'threshold' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{
                                transform: 'translate(50%, 50%)',
                                borderRadius: '2px',
                              }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'threshold', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>

                      {/* ACTIONS */}
                      <td className={`${lastColBorderClass} px-2 py-2 text-center`}>
                        <button
                          onClick={() => handleRemoveRow(originalIndex)}
                          disabled={brandConfig.thresholds.length === 1}
                          className={`${
                            brandConfig.thresholds.length === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-red-500 hover:text-red-700'
                          }`}
                        >
                          <TrashAlt className="h-4 w-4" />
                        </button>
                      </td>
                    </SortableThresholdRow>
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>

      <div className="flex justify-between mt-4">
        <Button
          variant="primary"
          onClick={() => onAddThresholdRow(brandConfig.brandId)}
          disabled={isDisabled}
        >
          Add Row
        </Button>
      </div>

      {/* Warning Dialog for switching to All Dimension */}
      <Dialog open={showAllDimensionWarning} onOpenChange={setShowAllDimensionWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch to All Dimensions?</DialogTitle>
            <DialogDescription>
              <span className="block">
                Are you sure you want to switch to All Dimensions? This will remove existing
                threshold rows with specific dimension configurations.
              </span>
            </DialogDescription>
          </DialogHeader>
          {pendingAllDimensionChange && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-semibold">
                  {pendingAllDimensionChange.rowsToRemove} threshold row
                  {pendingAllDimensionChange.rowsToRemove > 1 ? 's' : ''} will be removed.
                </span>
              </p>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={handleCancelAllDimension}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmAllDimension}>
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog for Select All */}
      <Dialog open={showSelectAllWarning} onOpenChange={setShowSelectAllWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select All Dimensions?</DialogTitle>
            <DialogDescription>
              <span className="block">
                Are you sure you want to select all dimensions? This will create a threshold row for
                each individual dimension.
              </span>
            </DialogDescription>
          </DialogHeader>
          {pendingSelectAllChange && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-semibold">
                  {pendingSelectAllChange.rowsToAdd} threshold row
                  {pendingSelectAllChange.rowsToAdd > 1 ? 's' : ''} will be created.
                </span>
              </p>
              {pendingSelectAllChange.rowsToRemove > 0 && (
                <p className="pt-2 border-t border-gray-200">
                  <span className="font-semibold">
                    {pendingSelectAllChange.rowsToRemove} existing row
                    {pendingSelectAllChange.rowsToRemove > 1 ? 's' : ''} with &quot;All
                    Dimensions&quot; will be removed.
                  </span>
                </p>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={handleCancelSelectAll}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSelectAll}>Proceed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
